import { assign, sendParent, setup } from 'xstate';
import type { ViewScheduledBusesQueryQuery } from '@/lib/gql/__generated__/graphql';
import type { StopSelectionEvent } from './events';
import {
  chooseDirectionActor,
  chooseRouteActor,
  chooseStopActor,
  refreshScheduledBusesActor,
  viewScheduledBusesActor,
} from './stop-selection.actors';

type SchedulePublishedEvent = { type: 'schedule.published' };
type View = ViewScheduledBusesQueryQuery['viewScheduledBuses'];

export type StopSelectionContext = {
  view: View | null;
  error: string | null;
  routeInput: { scheduleId: string; routeId: string } | null;
  directionInput: { stopSelectionId: string; directionId: string } | null;
  stopInput: { stopSelectionId: string; stopId: string } | null;
  refreshInput: { stopSelectionId: string } | null;
  stopSelectionId: string | null;
};

const initialContext: StopSelectionContext = {
  view: null,
  error: null,
  routeInput: null,
  directionInput: null,
  stopInput: null,
  refreshInput: null,
  stopSelectionId: null,
};

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'The request could not be completed.';

export const stopSelectionMachine = setup({
  types: {
    context: {} as StopSelectionContext,
    events: {} as StopSelectionEvent | SchedulePublishedEvent,
  },
  actors: {
    viewScheduledBusesActor,
    chooseRouteActor,
    chooseDirectionActor,
    chooseStopActor,
    refreshScheduledBusesActor,
  },
}).createMachine({
  id: 'stop-selection',
  initial: 'idle',
  context: initialContext,
  on: {
    'stop-selection.load-view': { target: '.loading', actions: assign({ error: null }) },
    'schedule.published': { target: '.loading', actions: assign({ error: null }) },
    'stop-selection.choose-route': {
      target: '.choosingRoute',
      actions: assign({
        routeInput: ({ event }) => ({ scheduleId: event.scheduleId, routeId: event.routeId }),
        error: null,
      }),
    },
    'stop-selection.choose-direction': {
      target: '.choosingDirection',
      actions: assign({
        directionInput: ({ event }) => ({ stopSelectionId: event.stopSelectionId, directionId: event.directionId }),
        error: null,
      }),
    },
    'stop-selection.choose-stop': {
      target: '.choosingStop',
      actions: assign({
        stopInput: ({ event }) => ({ stopSelectionId: event.stopSelectionId, stopId: event.stopId }),
        error: null,
      }),
    },
    'stop-selection.refresh': {
      target: '.refreshing',
      actions: assign({ refreshInput: ({ event }) => ({ stopSelectionId: event.stopSelectionId }), error: null }),
    },
  },
  states: {
    idle: {},
    loading: {
      invoke: {
        src: 'viewScheduledBusesActor',
        onDone: { target: 'ready', actions: assign({ view: ({ event }) => event.output, error: null }) },
        onError: { target: 'error', actions: assign({ error: ({ event }) => errorMessage(event.error) }) },
      },
    },
    ready: {},
    error: {},
    choosingRoute: {
      invoke: {
        src: 'chooseRouteActor',
        input: ({ context }) => context.routeInput ?? { scheduleId: '', routeId: '' },
        onDone: {
          target: 'loading',
          actions: [
            assign({ stopSelectionId: ({ event }) => event.output, error: null }),
            sendParent(({ event }) => ({ type: 'selection.route-chosen', stopSelectionId: event.output })),
          ],
        },
        onError: { target: 'error', actions: assign({ error: ({ event }) => errorMessage(event.error) }) },
      },
    },
    choosingDirection: {
      invoke: {
        src: 'chooseDirectionActor',
        input: ({ context }) => context.directionInput ?? { stopSelectionId: '', directionId: '' },
        onDone: {
          target: 'loading',
          actions: [
            sendParent(({ context }) => ({
              type: 'selection.direction-chosen',
              stopSelectionId: context.directionInput?.stopSelectionId ?? '',
            })),
          ],
        },
        onError: { target: 'error', actions: assign({ error: ({ event }) => errorMessage(event.error) }) },
      },
    },
    choosingStop: {
      invoke: {
        src: 'chooseStopActor',
        input: ({ context }) => context.stopInput ?? { stopSelectionId: '', stopId: '' },
        onDone: {
          target: 'loading',
          actions: [
            sendParent(({ context }) => ({
              type: 'selection.stop-chosen',
              stopSelectionId: context.stopInput?.stopSelectionId ?? '',
            })),
          ],
        },
        onError: { target: 'error', actions: assign({ error: ({ event }) => errorMessage(event.error) }) },
      },
    },
    refreshing: {
      invoke: {
        src: 'refreshScheduledBusesActor',
        input: ({ context }) => context.refreshInput ?? { stopSelectionId: '' },
        onDone: {
          target: 'loading',
          actions: [
            sendParent(({ context }) => ({
              type: 'scheduled-buses.calculated',
              stopSelectionId: context.refreshInput?.stopSelectionId ?? '',
            })),
          ],
        },
        onError: { target: 'error', actions: assign({ error: ({ event }) => errorMessage(event.error) }) },
      },
    },
  },
});
